import React, { useState, useEffect } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useReadContract } from 'wagmi';
import ConsensusProposalModal from '../FormModals/ConsensusProposalModal.tsx';
import communitiesConsensusContractABI from '../../contracts/tgetherCommunityConsensus.json';
import SetConsensusModal from '../FormModals/SetConsensusModal.tsx';

interface ConsensusInfoType {
    numReviewsForAcceptance: number;
    credsNeededForReview: number;
    percentAcceptsNeeded: number;
    consensusTime: number;
    consensusTypes: string[];
    isSet: boolean;
}

function ConsensusParams({ community, isOwner }: { community: string, isOwner: boolean }) {
    const [consensusInfo, setConsensusInfo] = useState<ConsensusInfoType | null>(null);
    const [showConsensusProposalModal, setShowConsensusProposalModal] = useState(false);
    const [showSetConsensusModal, setShowSetConsensusModal] = useState(false);

    const communitiesConsensusContractAddress = process.env.REACT_APP_COMMUNITY_CONSENSUS_CONTRACT_ADDRESS as `0x${string}`;

    // Fetch consensus info
    const { data: consensusData, isSuccess: consensusSuccess } = useReadContract({
        address: communitiesConsensusContractAddress,
        abi: communitiesConsensusContractABI.abi,
        functionName: 'getCCParams',
        args: [community],
    });

    useEffect(() => {
        if (consensusSuccess && consensusData) {
            const [
                _numReviewsForAcceptance,
                _credsNeededForReview,
                _percentAcceptsNeeded,
                _consensusTime,
                _consensusTypes,
                _isSet
            ] = consensusData as any; // Treat it as an array

            setConsensusInfo({
                numReviewsForAcceptance: Number(_numReviewsForAcceptance),
                credsNeededForReview: Number(_credsNeededForReview),
                percentAcceptsNeeded: Number(_percentAcceptsNeeded),
                consensusTime: Number(_consensusTime),
                consensusTypes: _consensusTypes, // Assuming it's already an array of strings
                isSet: Boolean(_isSet),
            });
        }
    }, [consensusSuccess, consensusData]);

    return (
        <Card style={{
            margin: '20px auto',
            padding: '20px',
            maxWidth: '600px',
            backgroundColor: '#1f1f1f',
            borderRadius: '8px',
            color: '#ffffff',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
        }}>
            <h5 style={{
                fontWeight: 'bold',
                marginBottom: '15px',
                backgroundColor: '#2a2a2a',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '4px',
                textAlign: 'center'
            }}>
                Consensus Parameters
            </h5>
            {consensusInfo && consensusInfo.isSet ? (
                <div style={{ lineHeight: '1.8' }}>
                    <Card.Text><strong>Num Reviews for Acceptance:</strong> <span className="badge bg-info">{consensusInfo.numReviewsForAcceptance}</span></Card.Text>
                    <Card.Text><strong>Creds Needed for Review:</strong> <span className="badge bg-success">{consensusInfo.credsNeededForReview}</span></Card.Text>
                    <Card.Text><strong>Percent Accepts Needed:</strong> <span className="badge bg-warning">{consensusInfo.percentAcceptsNeeded}%</span></Card.Text>
                    <Card.Text><strong>Consensus Time:</strong> <span className="badge bg-secondary">{consensusInfo.consensusTime}</span></Card.Text>
                    <Card.Text><strong>Consensus Types:</strong></Card.Text>
                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginBottom: '15px' }}>
                        {consensusInfo.consensusTypes.map((type, index) => (
                            <li key={index} style={{ color: '#f0ad4e' }}>{type}</li>
                        ))}
                    </ul>
                </div>
            ) : (
                <Card.Text>Consensus values for this community have yet to be set.</Card.Text>
            )}
            {isOwner && !consensusInfo?.isSet && (
                <Button variant="info" style={{ marginTop: '10px', width: '100%' }} onClick={() => setShowSetConsensusModal(true)}>
                    Set Consensus Parameters
                </Button>
            )}
            <Button
                variant="info"
                style={{ marginTop: '10px', width: '100%' }}
                onClick={() => setShowConsensusProposalModal(true)}
            >
                Propose New Consensus Parameters
            </Button>
            <ConsensusProposalModal
                show={showConsensusProposalModal}
                setShow={() => setShowConsensusProposalModal(false)}
                communityName={community}
            />
            <SetConsensusModal
                show={showSetConsensusModal}
                setShow={() => setShowSetConsensusModal(false)}
                communityName={community}
            />
        </Card>
    );
}

export default ConsensusParams;
